// ORGANIZAR A ORDEM DOS PRODUTOS DO FLY (pedido dele, 24/09/2026: *"poder re-ordenar a sequencia
// que os produtos aparecem no fly (...) de forma bonita"* + *"exibir a miniatura da foto do produto
// ao lado de cada um"*).
//
// ✅ **Nada muda no servidor**: a arte ja e' desenhada na ORDEM do array `produto_ids`
// (`fly-material.service`, "quem o operador marcou primeiro aparece primeiro"). Reordenar a lista
// aqui E' reordenar o fly — e a chave do material muda junto, porque a ordem entra nela
// (`fly-material.chave.test`), entao a arte nova nao reaproveita a velha do cache.
//
// ⚠️ **Dois gestos pro mesmo efeito, de proposito**: arrastar e' o natural no computador, mas no
// tablet o arrastar de HTML nao existe — as setas sobem/descem uma posicao e funcionam nos dois.
//
// ⚠️ A previa da direita imita a GRADE da arte: 12 = 3 colunas x 4 linhas
// (`gerar-catalogo-promocional`, `repeat(3, 1fr)`); 1 a 3 = lado a lado. Assim o numero da lista
// e a posicao no fly sao a mesma coisa a olho.
//
// ⛔ Mexe numa COPIA: "Aplicar" devolve a ordem nova, "Cancelar"/Esc descarta. Nada grava aqui —
// a campanha continua sendo gravada pelo botao dela, como qualquer outra alteracao.
import { useState } from 'react';
import { ArrowDown, ArrowUp, GripVertical, Image as ImageIcon } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { FotoProduto } from '../../components/FotoProduto';

export interface ProdutoOrdem {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  preco_pix: string;
  foto_url: string | null;
}

function formatBRL(v: string | number): string {
  return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Miniatura({ url, tam }: { url: string | null; tam: number }) {
  if (url) return <FotoProduto url={url} tam={tam} style={{ flexShrink: 0, backgroundColor: '#fff', borderRadius: 6 }} />;
  return (
    <span className="flex items-center justify-center rounded" style={{ width: tam, height: tam, flexShrink: 0, backgroundColor: '#fff', color: '#94a3b8' }}>
      <ImageIcon size={Math.round(tam / 2.5)} />
    </span>
  );
}

/** O numero da posicao — par casado do cabecalho de grade, o mesmo nas duas colunas. */
function Posicao({ n, tam = 22 }: { n: number; tam?: number }) {
  return (
    <span
      className="flex items-center justify-center rounded-full font-bold"
      style={{ width: tam, height: tam, flexShrink: 0, fontSize: tam * 0.52, backgroundColor: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}
    >
      {n}
    </span>
  );
}

export function ModalOrdemProdutosFly({ produtos, onAplicar, onFechar }: {
  produtos: ProdutoOrdem[];
  onAplicar: (ordem: ProdutoOrdem[]) => void;
  onFechar: () => void;
}) {
  const [lista, setLista] = useState<ProdutoOrdem[]>(produtos);
  const [arrastando, setArrastando] = useState<number | null>(null);
  const mudou = lista.some((p, i) => p.id !== produtos[i]?.id);

  function mover(de: number, para: number) {
    if (para < 0 || para >= lista.length || de === para) return;
    setLista((atual) => {
      const nova = [...atual];
      const [item] = nova.splice(de, 1);
      nova.splice(para, 0, item!);
      return nova;
    });
  }

  // Catalogo (4+) desenha em 3 colunas; ate 3, lado a lado numa linha so'.
  const colunasPrevia = lista.length > 3 ? 3 : Math.max(1, lista.length);

  const rodape = (
    <div className="flex items-center justify-between gap-3 w-full">
      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Arraste pela alça ou use as setas. A ordem vale depois de salvar a campanha.
      </span>
      <div className="flex gap-2">
        <button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>
        <button type="button" className="btn-primario" disabled={!mudou} onClick={() => { onAplicar(lista); onFechar(); }}>
          Aplicar ordem
        </button>
      </div>
    </div>
  );

  return (
    <Modal aberto onFechar={onFechar} titulo="Ordem dos produtos no fly" largura={1040} rodape={rodape} compacto bodyStyle={{ overflow: 'hidden' }}>
      <div className="grid gap-5" style={{ gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)' }}>
        {/* ── A LISTA: numero, alca, foto, codigo, descricao, preco e as setas ──
            ⛔ Rola POR DENTRO, com teto pela janela: esta tela abre por cima da campanha (que ja
            ocupa a tela), e com 12 linhas soltas o modal encostava no topo e o TITULO sumia atras
            da barra de menus (medido em prod, 24/09/2026: cabecalho em 8..57, barra ate 58). */}
        <div className="flex flex-col gap-1" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto', paddingRight: 4 }}>
          {lista.map((p, i) => (
            <div
              key={p.id}
              draggable
              onDragStart={(e) => { setArrastando(i); e.dataTransfer.effectAllowed = 'move'; }}
              onDragOver={(e) => {
                e.preventDefault();
                if (arrastando === null || arrastando === i) return;
                mover(arrastando, i);
                setArrastando(i);
              }}
              onDragEnd={() => setArrastando(null)}
              className="flex items-center gap-2.5 rounded-lg border px-2 py-1"
              style={{
                borderColor: 'var(--border)',
                // O item em movimento no par fixo da casa, como a linha marcada ([[user_daltonismo]]).
                backgroundColor: arrastando === i ? '#fde68a' : 'var(--panel-bg)',
                color: arrastando === i ? '#000' : undefined,
                cursor: 'grab',
              }}
            >
              <GripVertical size={16} style={{ flexShrink: 0, color: arrastando === i ? '#000' : 'var(--text-muted)' }} />
              <Posicao n={i + 1} />
              <Miniatura url={p.foto_url} tam={40} />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold truncate" title={p.descricao}>{p.descricao}</div>
                <div className="text-[11px]" style={{ color: arrastando === i ? '#000' : 'var(--text-muted)' }}>
                  {p.codigo_interno ? `Cód ${p.codigo_interno} · ` : ''}R$ {formatBRL(p.preco_pix)}
                </div>
              </div>
              <div className="flex flex-col" style={{ flexShrink: 0 }}>
                <button type="button" className="btn-icon" disabled={i === 0} onClick={() => mover(i, i - 1)} title="Subir uma posição" aria-label="Subir uma posição">
                  <ArrowUp size={14} />
                </button>
                <button type="button" className="btn-icon" disabled={i === lista.length - 1} onClick={() => mover(i, i + 1)} title="Descer uma posição" aria-label="Descer uma posição">
                  <ArrowDown size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ── A PREVIA: onde cada um cai na arte ── */}
        <div>
          <div className="text-xs font-bold uppercase mb-2" style={{ color: 'var(--text-muted)' }}>Como fica no fly</div>
          <div
            className="grid gap-2 rounded-xl p-3"
            style={{ gridTemplateColumns: `repeat(${colunasPrevia}, minmax(0, 1fr))`, backgroundColor: '#c81e1e' }}
          >
            {lista.map((p, i) => (
              <div key={p.id} className="relative rounded-lg flex flex-col items-center p-1.5" style={{ backgroundColor: '#fff', color: '#000' }}>
                <span className="absolute" style={{ top: 4, left: 4 }}><Posicao n={i + 1} tam={20} /></span>
                <Miniatura url={p.foto_url} tam={64} />
                <div className="text-[10px] font-bold text-center leading-tight mt-1 w-full" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {p.descricao}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
